import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, MessageCircle, Mail, Loader2, CheckCheck, Check, X } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function Notifications() {
  const { t } = useLang();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [respondedInvites, setRespondedInvites] = useState(new Set());

  const load = async () => {
    try {
      const res = await api.get("/notifications");
      setItems(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);

  const markRead = async (n) => {
    if (n.read) return;
    try { await api.post(`/notifications/${n.id}/read`); } catch {}
  };

  const openLink = async (n) => {
    await markRead(n);
    if (n.link && (n.type === "group_message" || n.type === "dm" || n.type === "connection_accepted")) {
      nav(n.link);
    }
  };

  const respond = async (n, accept) => {
    if (!n.ref_id) return;
    setBusyId(n.id);
    try {
      await api.post(`/invites/${n.ref_id}/respond`, { accept });
      await markRead(n);
      setRespondedInvites((s) => new Set(s).add(n.ref_id));
      toast.success(accept ? t("accept") : t("reject"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("networkErr"));
    } finally { setBusyId(null); }
  };

  const respondConnect = async (n, accept) => {
    if (!n.ref_id) return;
    setBusyId(n.id);
    try {
      await api.post(`/connections/${n.ref_id}/respond`, { accept });
      await markRead(n);
      setRespondedInvites((s) => new Set(s).add(n.ref_id));
      toast.success(accept ? t("nowConnected") : t("decline"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("networkErr"));
    } finally { setBusyId(null); }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      toast.success(t("saved"));
      load();
    } catch {}
  };

  const unread = items.filter((n) => !n.read).length;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--secondary))] font-bold">{t("notifications")}</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight flex items-center gap-3">
            {t("notifications")}
            {unread > 0 && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-[hsl(var(--primary))] text-white" data-testid="unread-pill">
                {unread} {t("unread")}
              </span>
            )}
          </h1>
        </div>
        {unread > 0 && (
          <Button variant="outline" size="sm" onClick={markAll} data-testid="mark-all-read-btn" className="rounded-full border-[hsl(var(--border))] gap-1.5">
            <CheckCheck className="h-4 w-4" /> {t("markAllRead")}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-[hsl(var(--border))] rounded-3xl p-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="notif-empty">
          <Bell className="h-8 w-8 mx-auto text-[hsl(var(--primary))] mb-3" />
          {t("noNotifications")}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const isInvite = n.type === "invite";
            const isConnectReq = n.type === "connection_request";
            const Icon = (isInvite || isConnectReq) ? Mail : MessageCircle;
            const iconBg = (isInvite || isConnectReq) ? "bg-[hsl(var(--secondary))]" : "bg-[hsl(var(--primary))]";
            const alreadyResponded = (isInvite || isConnectReq) && respondedInvites.has(n.ref_id);
            return (
              <div
                key={n.id}
                data-testid={`notif-${n.id}`}
                className={`w-full flex items-start gap-3 p-4 rounded-2xl border transition-all ${
                  n.read
                    ? "bg-white border-[hsl(var(--border))]"
                    : "bg-[hsl(var(--elevated))] border-[hsl(var(--primary))]/30"
                }`}
              >
                <button
                  type="button"
                  onClick={() => openLink(n)}
                  className={`relative h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 ${iconBg} text-white`}
                  aria-label="Open"
                >
                  <Icon className="h-5 w-5" />
                  {!n.read && <span className="absolute -top-0.5 -end-0.5 h-2.5 w-2.5 rounded-full bg-[hsl(var(--primary))] border-2 border-white pulse-dot" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => openLink(n)}
                      className="text-start font-medium text-sm leading-snug hover:underline"
                    >
                      {n.title}
                    </button>
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))] shrink-0">{timeAgo(n.created_at)}</div>
                  </div>
                  {n.body && <div className="mt-0.5 text-sm text-[hsl(var(--muted-foreground))]">{n.body}</div>}

                  {isInvite && !alreadyResponded && (
                    <div className="mt-3 flex gap-2">
                      <Button
                        onClick={() => respond(n, true)}
                        disabled={busyId === n.id}
                        data-testid={`notif-accept-${n.id}`}
                        size="sm"
                        className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white"
                      >
                        {busyId === n.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        <span className="ms-1">{t("accept")}</span>
                      </Button>
                      <Button
                        onClick={() => respond(n, false)}
                        disabled={busyId === n.id}
                        data-testid={`notif-reject-${n.id}`}
                        size="sm"
                        variant="outline"
                        className="rounded-full border-[hsl(var(--border))]"
                      >
                        <X className="h-3.5 w-3.5 me-1" />
                        {t("reject")}
                      </Button>
                    </div>
                  )}
                  {isConnectReq && !alreadyResponded && (
                    <div className="mt-3 flex gap-2">
                      <Button
                        onClick={() => respondConnect(n, true)}
                        disabled={busyId === n.id}
                        data-testid={`notif-conn-accept-${n.id}`}
                        size="sm"
                        className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white"
                      >
                        {busyId === n.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        <span className="ms-1">{t("accept")}</span>
                      </Button>
                      <Button
                        onClick={() => respondConnect(n, false)}
                        disabled={busyId === n.id}
                        data-testid={`notif-conn-decline-${n.id}`}
                        size="sm"
                        variant="outline"
                        className="rounded-full border-[hsl(var(--border))]"
                      >
                        <X className="h-3.5 w-3.5 me-1" />
                        {t("decline")}
                      </Button>
                    </div>
                  )}
                  {(isInvite || isConnectReq) && alreadyResponded && (
                    <div className="mt-2 text-xs text-[hsl(var(--primary))] font-medium">✓ {t("responded")}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
